import api from './api/axiosConfig';

const adService = {
  // Get all ads with optional filters
  getAds: async (params = {}) => {
    try {
      const response = await api.get('/ads/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching ads:', error);
      throw error;
    }
  },

  // Get a single ad by ID
  getAd: async (id) => {
    try {
      const response = await api.get(`/ads/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching ad:', error);
      throw error;
    }
  },

  // Create a new ad
  createAd: async (adData, options = {}) => {
    try {
      const { clientRequestId } = options;
      const formData = new FormData();
      
      // Add basic fields
      Object.keys(adData).forEach(key => {
        if (key === 'images' && Array.isArray(adData[key])) {
          // Handle images separately
          adData[key].forEach((image, index) => {
            formData.append('images', image);
          });
        } else if (key !== 'images' && adData[key] !== undefined && adData[key] !== null) {
          const value =
            typeof adData[key] === 'boolean' ? String(adData[key]) : adData[key];
          formData.append(key, value);
        }
      });

      if (clientRequestId) {
        formData.append('client_request_id', clientRequestId);
      }

      const response = await api.post('/ads/', formData);
      return response.data;
    } catch (error) {
      console.error('Error creating ad:', error);
      throw error;
    }
  },

  // Update an ad
  updateAd: async (id, adData) => {
    try {
      const formData = new FormData();
      
      // Add basic fields
      Object.keys(adData).forEach(key => {
        if (key === 'images' && Array.isArray(adData[key])) {
          // Handle images separately
          adData[key].forEach((image, index) => {
            formData.append('images', image);
          });
        } else if (key !== 'images' && adData[key] !== undefined && adData[key] !== null) {
          const value =
            typeof adData[key] === 'boolean' ? String(adData[key]) : adData[key];
          formData.append(key, value);
        }
      });

      const response = await api.put(`/ads/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating ad:', error);
      throw error;
    }
  },

  // Delete an ad
  deleteAd: async (id) => {
    try {
      const response = await api.delete(`/ads/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting ad:', error);
      throw error;
    }
  },

  // Delete a single image from an ad (edit flow: replace/remove images)
  deleteAdImage: async (adSlug, imageId) => {
    try {
      await api.delete(`/ads/${adSlug}/images/${imageId}/`);
    } catch (error) {
      console.error('Error deleting ad image:', error);
      throw error;
    }
  },

  // Get ads by category
  getAdsByCategory: async (category, filters = {}) => {
    try {
      const params = {
        category,
        ...filters
      };
      const response = await api.get('/ads/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching ads by category:', error);
      throw error;
    }
  },

  // Get ads by price range
  getAdsByPriceRange: async (minPrice, maxPrice, filters = {}) => {
    try {
      const params = {
        price_min: minPrice,
        price_max: maxPrice,
        ...filters
      };
      const response = await api.get('/ads/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching ads by price range:', error);
      throw error;
    }
  },

  // Search ads
  searchAds: async (query, filters = {}) => {
    try {
      const params = {
        search: query,
        ...filters
      };
      const response = await api.get('/ads/', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching ads:', error);
      throw error;
    }
  },

  // Get comments for a specific ad
  getAdComments: async (id) => {
    try {
      const response = await api.get(`/ads/${id}/comments/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching ad comments:', error);
      throw error;
    }
  },

  // Create a new comment on an ad
  createAdComment: async (id, text) => {
    try {
      const response = await api.post(`/ads/${id}/comments/`, { text });
      return response.data;
    } catch (error) {
      console.error('Error creating ad comment:', error);
      throw error;
    }
  },

  // Get ad categories
  getCategories: () => {
    return [
      { value: 'car', label: 'Car' },
      { value: 'clothing', label: 'Clothing' },
      { value: 'electronics', label: 'Electronics' },
      { value: 'property', label: 'Property' },
      { value: 'services', label: 'Services' },
      { value: 'other', label: 'Other' },
    ];
  },

  // Increment ad views
  incrementViews: async (id) => {
    try {
      const response = await api.post(`/ads/${id}/increment-views/`);
      return response.data;
    } catch (error) {
      console.error('Error incrementing views:', error);
      throw error;
    }
  },

  // Get condition options for different categories
  getConditionOptions: (category) => {
    const conditions = {
      car: [
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
      clothing: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
      electronics: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
      property: [
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'needs_renovation', label: 'Needs Renovation' },
        { value: 'poor', label: 'Poor' },
      ],
      default: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ]
    };
    return conditions[category] || conditions.default;
  }
};

export { adService };
